import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link as GatsbyLink } from "gatsby";
import { AiOutlineCloseCircle } from "react-icons/ai";

import { Maybe, SanityHero, SanityNews, SanitySlug } from "@graphql-types";
import { AbsoluteWrapper, Container, GradientOverlay, P } from "@util/standard";
import { BlocksContent, Image, Link, Video } from "@global";
import { animations, colors, mediaQuery, sectionAttributes } from "@util/constants";
import { CircleArrow, PlayButton, ScrollIcon } from "@util/assets";

interface Props {
  data: Maybe<SanityHero> | undefined;
  useFullHeight?: boolean;
  articleData?: SanityNews[];
  slug?: Maybe<SanitySlug> | undefined;
}

const TextWrapper = styled(AbsoluteWrapper)<{ isArticleStyle?: boolean; hasVideo?: boolean }>`
  width: 80%;
  left: 10%;
  bottom: 12%;
  ${({ isArticleStyle }) => isArticleStyle && `bottom: 50%; transform: translateY(50%);`};
  ${({ isArticleStyle, hasVideo }) =>
    isArticleStyle && hasVideo && `bottom: 20%; transform: translateY(20%);`};

  p,
  h1,
  h2,
  h3,
  h4.h5 {
    color: ${colors.white};
    width: 35%;

    ${({ isArticleStyle }) =>
      isArticleStyle && `margin: auto !important; text-align: center; width: 50%;`}
    ${mediaQuery.largeDown} {
      width: 60%;
    }
    ${mediaQuery.mediumDown} {
      width: 100%;
    }
  }

  ${mediaQuery.smallDown} {
    ${({ isArticleStyle, hasVideo }) =>
      isArticleStyle && hasVideo && `bottom: 35%; transform: translateY(35%);`};
  }
  ${({ isArticleStyle }) => !isArticleStyle && `${animations.slideInFromLeft}`}
`;

const IconWrapper = styled(AbsoluteWrapper)`
  right: 3%;
  bottom: 4%;

  ${animations.bounce}
`;

const CloseButton = styled.button<{ isMobileVideo?: string | null }>`
  position: absolute;
  top: 50%;
  left: 2%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${colors.white};
  font-size: 18px;
  opacity: 1;

  svg {
    transform: translateY(5px);
    margin-right: 5px;
    font-size: 25px;
  }
  &:hover {
    opacity: 0.5;
  }
  ${animations.slideInFromLeft}

  ${mediaQuery.largeDown} {
    top: 20%;
  }
  ${mediaQuery.smallDown} {
    top: 25%;
  }

  ${({ isMobileVideo }) =>
    isMobileVideo &&
    `
    ${mediaQuery.mediumDown} {
      top: unset;
      bottom: 7%;
    }
  `}
`;

const ArrowNavWrapper = styled(AbsoluteWrapper)`
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.white};
  p {
    width: 59px;
    text-align: center;
    margin-top: 20px;
    line-height: 20px;
  }

  ${mediaQuery.largeDown} {
    top: unset;
    bottom: 20%;
    transform: translateY(0%);
  }
  ${mediaQuery.mediumDown} {
    top: unset;
    bottom: 15%;
    transform: translateY(0%);
    p {
      margin-top: 5px;
    }
  }
`;

function Hero({ data, useFullHeight, articleData, slug }: Props) {
  if (data == null) return null;

  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);

  const { video, mobileVideo, image, blockContent, links, marginBottom, _key } = data;

  const { sectionAttr } = sectionAttributes(marginBottom, _key);

  const videoExistsAndVideoPlaying = video?.url != null && videoPlayerOpen;

  const findCurrentArticle = () => {
    if (articleData == null || slug === null) return null;

    const currentArticleIndex = articleData?.findIndex(article => {
      if (article == null) return null;
      return article.slug?.current === slug;
    });

    if (currentArticleIndex) {
      setCurrentArticleIndex(currentArticleIndex);
    }
    return;
  };

  const formatArticleLink = (index: number) => {
    if (articleData == null || articleData[index] == null) return null;

    const defaultIban = process.env.GATSBY_DEFAULT_IBAN_REGION as string;
    const { region, slug } = articleData[index];
    const formattedArticleLink = `/${region?.iban + "/" ?? ""}${slug?.current}`;

    return defaultIban === region?.iban ? `/${slug?.current}` : formattedArticleLink;
  };

  const toNext = formatArticleLink(currentArticleIndex + 1);
  const toPrev = formatArticleLink(currentArticleIndex - 1);

  const organiseOverlayDarkness = (): number => {
    if (videoPlayerOpen) return 1;
    if (image) return 0.4;
    return 1;
  };

  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }

    setWidth(window.innerWidth);

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  useEffect(() => {
    findCurrentArticle();
  }, []);

  return (
    <section {...sectionAttr}>
      <Container
        height={useFullHeight ? "100vh" : "80vh"}
        width="100%"
        backgroundColor="red"
        className={`${mobileVideo?.url ? "hero-mobile-video" : ""}`}
      >
        <Container width="100%" height="100%" position="relative">
          <Container>
            {image && <Image data={image} isBackground />}
            {(video?.url || mobileVideo?.url) && (
              <Video
                key={width > 768 ? video?.url : mobileVideo?.url ? mobileVideo?.url : video?.url}
                url={width > 768 ? video?.url : mobileVideo?.url ? mobileVideo?.url : video?.url}
                autoPlayOnLoad
                volume={videoPlayerOpen}
                currentTime={videoPlayerOpen ? 0 : null}
                foregroundOpen={videoPlayerOpen}
                isMobileVideo={mobileVideo?.url}
              />
            )}
          </Container>
          {!videoExistsAndVideoPlaying && <GradientOverlay gradient="fadeFromTop" />}
          {!videoExistsAndVideoPlaying && (
            <TextWrapper isArticleStyle={articleData != null} hasVideo={video?.url != null}>
              <BlocksContent data={blockContent} />
              {links && (
                <Container margin="35px 0 0 0" flexWrap="wrap">
                  {links.map(link => {
                    if (link == null) return null;

                    return (
                      <Container margin="0 15px 15px 0" key={link._key}>
                        <Link {...link} design="filledLight" />
                      </Container>
                    );
                  })}
                </Container>
              )}
            </TextWrapper>
          )}
          {videoPlayerOpen ? (
            <CloseButton onClick={() => setVideoPlayerOpen(false)} isMobileVideo={mobileVideo?.url}>
              <AiOutlineCloseCircle />
              Close
            </CloseButton>
          ) : !videoPlayerOpen && video?.url ? (
            <PlayButton onClick={() => setVideoPlayerOpen(true)} />
          ) : null}
          <IconWrapper>
            <ScrollIcon />
          </IconWrapper>

          {articleData && !videoExistsAndVideoPlaying && (
            <Container>
              {toPrev && (
                <ArrowNavWrapper left="6%">
                  <GatsbyLink to={toPrev as string}>
                    <CircleArrow />
                    <p>Previous Article</p>
                  </GatsbyLink>
                </ArrowNavWrapper>
              )}
              {toNext && (
                <ArrowNavWrapper right="6%">
                  <GatsbyLink to={toNext as string}>
                    <CircleArrow flip />
                    <p>Next Article</p>
                  </GatsbyLink>
                </ArrowNavWrapper>
              )}
            </Container>
          )}
        </Container>
      </Container>
    </section>
  );
}

export default Hero;
